
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

import { type CartFeeLine } from '@tix/cart'

import { type PayButtonContext, onSubmit } from '@/helpers/CartHelpers'
import { formatCurrency } from '@/helpers/Currency'
import { formatCartItemDateTime } from '@/helpers/Date'
import { theme } from '@/helpers/Environment'
import { type FlowDisplayItem, groupFlowDisplayItems } from '@/helpers/FlowHelpers'
import { sum } from '@/helpers/Math'

import ExpiryTimer from '@/components/cart/ExpiryTimer.vue'
import Price from '@/components/elements/Price.vue'

import type { TixTimeDuration } from '@/TixTime/Duration'
import ApplyPromoCodes from '@/checkout/components/ApplyPromoCodes.vue'
import type { WalletType } from '@/checkout/helpers/wallets'
import StripeWalletPayment from '@/checkout/stripe/StripeWalletPayment'
import type { PaymentMethodName } from '@/checkout/stripe/helpers'
import type { LanguageStrings } from '@/language/types'
import { cartExpiresIn } from '@/state/Cart'
import type { PendingGiftCardPayment } from '@/store/Cart'
import { CartItem, TimedItem, isTimedItem } from '@/store/CartItem'
import store from '@/store/store'
import type { ComponentOptions } from '@/types/ComponentOptions'

import PayButton from './PayButton.vue'
import RemoveCartItemOverlay from './RemoveCartItemOverlay.vue'

type CartWidgetItem = CartItem & {
  isPendingRemoval?: boolean
}

@Component({
  name: 'CartWidget',
  components: {
    ExpiryTimer,
    ApplyPromoCodes,
    Price,
    RemoveCartItemOverlay,
    PayButton,
  },
  computed: mapGetters({
    memberIdentity: 'Member/identity',
    cart: 'Cart/cart',
    cartItems: 'Cart/cartItems',
    cartMods: 'Cart/cartMods',
    faceValue: 'Cart/faceValue',
    cartFeeItems: 'Cart/cartFeeItems',
    totalPromoDiscounts: 'Cart/totalPromoDiscounts',
    totalFixedFees: 'Cart/totalFixedFees',
    totalPercentFees: 'Cart/totalPercentFees',
    totalPayments: 'Cart/totalPayments',
    paymentDue: 'Cart/paymentDue',
    pendingGiftCardPayments: 'Cart/pendingGiftCardPayments',
    paymentDueByCreditCard: 'Cart/paymentDueByCreditCard',
    flowDefinitions: 'Cart/flowDefinitions',
  }),
  methods: {
    formatCurrency,
  },
})
export default class extends Vue {
  t: LanguageStrings['cartWidget']
  opt: ComponentOptions['cartWidget']

  @Prop({ default: false })
  submitDisabled: boolean

  @Prop({ default: 'default' })
  context: PayButtonContext

  @Prop()
  selectedPaymentType: PaymentMethodName

  @Prop()
  supportedWallet: WalletType

  @Prop({ default: false })
  submitting: boolean

  @Prop({ default: false })
  isEmbedded: boolean

  @Prop()
  wallet: StripeWalletPayment | null

  // Let TypeScript know about the getters.
  // Unfortunately vuex-class doesn't work with modules
  // @see https://github.com/ktsn/vuex-class
  pendingGiftCardPayments: PendingGiftCardPayment[]
  pendingRemovalCartItemId: string | null = null
  pendingRemovalTriggerElement: HTMLElement | null = null
  cart
  cartItems: CartItem[]
  cartMods: CartMod[]
  faceValue
  cartFeeItems: CartFeeLine[]
  totalPayments
  paymentDue
  paymentDueByCreditCard
  totalFixedFees
  totalPercentFees
  totalPromoDiscounts
  flowDefinitions: Record<string, FlowDefinition>

  expiresIn: TixTimeDuration | null = null
  expiryInterval: ReturnType<typeof setTimeout>

  distanceTopViewport = 0

  created() {
    this.setExpiry()

    this.expiryInterval = setInterval(this.setExpiry, 1000)
  }

  mounted() {
    this.setDistanceTopViewport()
    window.addEventListener('scroll', this.setDistanceTopViewport)
  }

  setDistanceTopViewport() {
    const cart = this.$refs.cart as HTMLElement

    if (!this.isEmbedded) {
      const rect = cart.getBoundingClientRect()
      // Minimum value of 20 as the .aside has `top: 20px`.
      this.distanceTopViewport = Math.max(rect.top, 20)
    }
  }

  @Watch('distanceTopViewport')
  setMaxHeight() {
    const cart = this.$refs.cart as HTMLElement
    // Give it a 20px margin on the bottom.
    // .aside has `position: sticky` and `top: 20px` ensuring there is a 20px margin at the top.
    cart.style.maxHeight = `max(500px, calc(100vh - ${this.distanceTopViewport + 20}px))`
  }

  destroyed() {
    clearInterval(this.expiryInterval)
    window.removeEventListener('scroll', this.setDistanceTopViewport)
  }

  get empty() {
    return this.cartItems.length < 1
  }

  get promoCodeApplyEnabled() {
    return this.isModifiable && this.paymentDue > 0 && this.totalPayments === 0 && !this.blocked
  }

  get showTotals(): boolean {
    const cartFeeTotal = sum(this.cartFeeItems.map((line) => line.amount))
    return this.paymentDueByCreditCard !== 0 || this.faceValue !== 0 || cartFeeTotal > 0
  }

  get discounts() {
    const feeTicketIds = new Set(this.cartFeeItems.flatMap((fee) => fee.ticketIds))

    return (
      this.cartMods
        // Hide checkout rules that block checkout but don't discount the value.
        // Show checkout rules that don't block checkout or offer a discount.
        // They probably provide other benefits, such as extended two-year membership.
        .filter((mod) => !(mod.blocking_checkout && Number(mod.discount) === 0))
        // The add_ticket fee rule records the fee ticket with a 0 discount.
        // A later rule can discount those same tickets. Keep that discount.
        .filter((mod) => {
          const affected = mod.tickets_affected ?? []
          const affectsOnlyFees = affected.length > 0 && affected.every((ticketId) => feeTicketIds.has(ticketId))
          return !(affectsOnlyFees && Number(mod.discount) === 0)
        })
    )
  }

  setExpiry() {
    this.expiresIn = cartExpiresIn()
  }

  get items(): CartWidgetItem[] {
    return this.cartItems.map((item) => {
      const isPendingRemoval = item.id === this.pendingRemovalCartItemId

      const updatedItem: CartWidgetItem = { ...item, isPendingRemoval }

      if (item.isMembershipEvent) {
        const types = item.types.filter((type) => type.totalPrice > 0)
        return {
          ...updatedItem,
          // Membership cart items show the ticket group name instead of the event name
          name: types.length > 0 ? types[0].group.name : item.name,
          types,
        }
      } else {
        return {
          ...updatedItem,
          types: item.types.map((type) => ({
            ...type,
            name: this.opt.showTicketGroupName ? `${type.group.name} - ${type.name}` : type.name,
          })),
        }
      }
    })
  }

  get hasUpsells() {
    return this.items.some((item) => !item.isAnchor)
  }

  itemDateTime(item: CartItem): string | null {
    if (!isTimedItem(item)) {
      return null
    }

    const dateTimeFormat = this.opt.dateTimeFormat ?? 'ddd, MMM D, YYYY [at] h:mm A'
    return formatCartItemDateTime(item as TimedItem, {
      dateTimeFormat,
      dateOnlyFormat: 'ddd, MMM D, YYYY',
      timeOnlyFormat: 'h:mm A',
    })
  }

  get displayItems(): FlowDisplayItem[] {
    return groupFlowDisplayItems(this.items, this.pendingRemovalCartItemId, this.flowDefinitions, (count) =>
      this.$tc('cartWidget.flowExperience', count, { count }),
    )
  }

  // Handler for click on pay button. Not the form-submit event.
  // TODO Document why. Does Apple Pay button submit the <form>?
  // @see https://tixtrackteam.slack.com/archives/C02CMPTFLKF/p1746147319477799?thread_ts=1746146976.910289&cid=C02CMPTFLKF
  onSubmit() {
    onSubmit(this.context, this.$router, () => this.$emit('submit'))
  }

  get darkTheme(): boolean {
    return theme?.cartThemeMode === 'dark' || theme?.themeMode === 'dark'
  }

  get isModifiable(): boolean {
    return store.getters['Cart/isModifiable']
  }

  get hasScheduledPayments(): boolean {
    return this.$store.getters['Cart/hasScheduledPayments']
  }

  get outstandingBalance(): number {
    return this.$store.getters['Cart/outstandingBalance']
  }

  get blocked(): boolean {
    return store.getters['Cart/blockingRules']?.length > 0
  }

  get hidePayButton() {
    return this.context === 'checkout' && this.blocked
  }

  handleRemoveItem(item: CartItem, index: number) {
    this.pendingRemovalCartItemId = item.id
    this.pendingRemovalTriggerElement = this.$refs[`removeButton_${index}`][0] as HTMLElement
  }

  handleRemoveItemClose() {
    this.pendingRemovalCartItemId = null
    this.pendingRemovalTriggerElement = null
  }
}
